import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Gift, Music4, Sparkles, Loader2, Heart, Check } from "lucide-react";
import { api } from "@/lib/api";
import { LOGO, PHOTOS } from "@/lib/constants";
import { Reveal } from "@/components/landing/Reveal";
import { FloatingOrbs } from "@/components/landing/FloatingOrbs";

const BENEFITS = [
  { icon: CalendarClock, title: "Date in anteprima assoluta", body: "Scopri dove e quando ci vediamo, prima di chiunque altro." },
  { icon: Gift, title: "Vantaggi esclusivi agli eventi", body: "Sorprese, priorità e piccoli privilegi riservati alla famiglia." },
  { icon: Music4, title: "Anteprime su nuove canzoni", body: "Ascolta i nuovi brani e le novità prima che diventino ufficiali." },
  { icon: Sparkles, title: "Sul palco in cosplay con noi", body: "La grande novità: salire sul palco in cosplay durante i concerti. Le Mermaid Melody, insieme a te." },
];

const inputCls = "w-full rounded-2xl border border-navy/10 bg-white/70 px-5 py-3.5 text-navy outline-none focus:border-lavender transition-colors placeholder:text-navy-faint";

export default function Community() {
  const [form, setForm] = useState({ nome: "", email: "", cap: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Accetta l'informativa privacy per continuare.");
    setLoading(true);
    try {
      await api.post("/community/subscribe", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Iscrizione non riuscita, riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-[100svh] overflow-hidden" data-testid="community-page">
      <div className="aurora" aria-hidden />
      <FloatingOrbs />

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12 pt-6">
        <Link to="/" data-testid="community-logo" className="flex items-center gap-2.5">
          <img src={LOGO} alt="Cosplay Singers" className="h-10 w-10 object-contain" />
          <span className="font-serif text-base sm:text-lg tracking-tightest text-navy">Cosplay Singers</span>
        </Link>
        <Link to="/" data-testid="community-back" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-navy-soft hover:text-lavender-deep transition-colors">
          <ArrowLeft size={15} /> Torna al sito
        </Link>
      </header>

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:px-12 py-14 sm:py-20 lg:grid-cols-2">
        {/* LEFT — copy + benefits */}
        <div>
          <Reveal>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-lavender-deep">
              <Heart size={13} className="fill-current" /> Mermaid Melody Italia — Community
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif tracking-tightest text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy">
              Entra nella famiglia delle <span className="text-lavender-deep">Mermaid Melody</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-navy-soft">
              Iscriviti alla community ufficiale e vivi lo show da protagonista. Ti scriviamo solo quando c’è qualcosa che vale davvero.
            </p>
          </Reveal>

          <div className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={0.15 + i * 0.06}>
                <div className="flex h-full items-start gap-3 rounded-3xl glass p-5" data-testid={`community-benefit-${i}`}>
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-lavender/15 text-lavender-deep">
                    <b.icon size={19} />
                  </span>
                  <div>
                    <p className="font-medium text-navy leading-snug">{b.title}</p>
                    <p className="mt-1 text-sm text-navy-soft leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* RIGHT — image + form */}
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2.25rem] glass-strong p-2.5">
              <img src={PHOTOS.stage} alt="Le Cosplay Singers sul palco" className="aspect-[4/3] w-full rounded-[1.75rem] object-cover" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 mx-auto -mt-14 w-[92%] rounded-[2rem] glass-strong p-7 sm:p-8"
            >
              {done ? (
                <div className="py-6 text-center" data-testid="community-success">
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-green/20 text-green-deep">
                    <Check size={26} />
                  </div>
                  <h3 className="font-serif text-2xl text-navy">Benvenuto nella famiglia.</h3>
                  <p className="mt-3 text-navy-soft">
                    Sei dentro. Ti scriviamo appena ci sono date, novità e la sorpresa del palco. Alcune canzoni finiscono, i ricordi no.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="community-form">
                  <h3 className="font-serif text-2xl text-navy">Iscriviti alla community</h3>
                  <p className="mt-1 mb-6 text-sm text-navy-soft">È gratis. Puoi disiscriverti quando vuoi.</p>

                  <div className="space-y-3">
                    <input data-testid="community-nome" placeholder="Nome" value={form.nome} onChange={set("nome")} className={inputCls} required />
                    <input data-testid="community-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} required />
                    <input data-testid="community-cap" inputMode="numeric" placeholder="CAP" value={form.cap} onChange={set("cap")} className={inputCls} required />
                  </div>

                  <label className="mt-5 flex items-start gap-3 text-sm text-navy-soft cursor-pointer">
                    <input
                      data-testid="community-consent"
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-navy/30 accent-lavender"
                    />
                    <span>
                      Accetto il trattamento dei miei dati per ricevere aggiornamenti dalla community, come descritto nella{" "}
                      <Link to="/privacy" className="text-lavender-deep underline underline-offset-2">Privacy Policy</Link>.
                    </span>
                  </label>

                  <button
                    data-testid="community-submit"
                    disabled={loading}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-lavender py-3.5 font-medium text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 glow-lav"
                  >
                    {loading ? <Loader2 size={17} className="animate-spin" /> : <Heart size={16} className="fill-current" />}
                    {loading ? "Iscrizione…" : "Entra nella famiglia"}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
